<template>
  <header id="header">
    <div class="area_top">
      <div class="inr">
        <div v-if="!isLoggedIn" class="util_member">
          <!-- 송은영 주무관 요청으로 테스트 텍스트 추가(2026-04-18) / 운영환경 미출력 -->
          <span v-if="!isProd" style="font-size:14px; color: #a5a5a5; font-weight: bold; text-align: left; height:24px; line-height: 14px; border: 1px solid rgb(239, 75, 85); border-radius: 4px; padding: 4px;">테스트</span>
          <a href="https://www.daegu.go.kr/" class="daegu" target="_blank" title="새창열림">
            <img src="@/assets/images/common/img_daegu.png" alt="대구광역시" />
          </a>
          <RouterLink to="/login" class="login">로그인</RouterLink>
          <RouterLink to="/join" class="join">회원가입</RouterLink>
        </div>
        <div v-else class="util_member">
          <a href="https://www.daegu.go.kr/" class="daegu" target="_blank" title="새창열림">
            <img src="@/assets/images/common/img_daegu.png" alt="대구광역시" />
          </a>
          <div id="sessionBox">
            로그아웃 남은 시간: <span id="session-timer">{{ mmss }}</span>
            <button
              id="extendBtn"
              class="button blue"
              type="button"
              :disabled="isExtendingSession"
              :aria-busy="isExtendingSession ? 'true' : 'false'"
              @click="handleExtend"
            >
              {{ isExtendingSession ? '처리 중' : '연장' }}
            </button>
            <span v-if="sessionExtendMessage" class="session_feedback" role="status" aria-live="polite">
              {{ sessionExtendMessage }}
            </span>
          </div>
          <RouterLink to="/my-rsvt/list" class="mypage">마이페이지</RouterLink>
          <a href="#" class="logout" @click.prevent="logout">로그아웃</a>
          <RouterLink to="/my-info-edit/detail" class="modify">정보수정</RouterLink>
        </div>
      </div>
    </div>

    <div class="area_logo">
      <h1 class="logo">
        <RouterLink to="/">대구광역시 통합예약시스템</RouterLink>
      </h1>
    </div>

    <nav class="gnb_wrap">
      <div class="inr">
        <ul id="gnb">
          <li v-for="sec in headerNav" :key="sec.menuId">
            <a
              :href="hrefForMenu(sec) || '#'"
              :target="sec.target || undefined"
              :rel="relFor(sec)"
              @click.prevent="navigate(sec)"
            >
              <span>{{ sec.menuNm }}</span>
            </a>

            <div v-if="sec.children?.length">
              <div class="inr">
                <div :class="sec.titAreaClass">
                  <div class="inr">
                    <strong class="tit">{{ sec.menuNm }}</strong>
                    <div v-dompurify-html="defaultTagline" class="txt"></div>
                  </div>
                </div>
                <div>
                  <ul>
                    <li v-for="child in visibleChildren(sec.children)" :key="child.menuId">
                      <RouterLink
                        :to="hrefForMenu(child) || '#'"
                        :target="child.menuType === 'LINK' ? '_blank' : child.target || undefined"
                        :rel="relFor(child)"
                        @click.prevent="navigate(child)"
                      >
                        <span>{{ child.menuNm }}</span>
                      </RouterLink>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </nav>

    <SitemapDrawer v-model:open="isSitemapOpen" />
    <SearchOverlay v-model:open="isSearchOpen" />
  </header>

  <div class="pop_logout">
    <div class="box_comm6">
      <div class="box_comm6_in">
        <p id="session_pop_info" class="tit">--:-- 후 자동 로그아웃됩니다.</p>
        <p class="txt">
          <span id="session_info_time">--:--</span> 동안 서비스를 이용하지 않아 <br />
          잠시 후 자동으로 로그아웃될 예정입니다. <br />
          로그인 시간을 연장하시겠습니까?
        </p>
        <div class="btn_area">
          <button id="logoutBtn" type="button" class="button lg" @click="logout">로그아웃</button>
          <button
            id="extendBtnPopup"
            type="button"
            class="button lg yellow"
            :disabled="isExtendingSession"
            :aria-busy="isExtendingSession ? 'true' : 'false'"
            @click="handleExtendPopup"
          >
            {{ isExtendingSession ? '처리 중' : '로그인 연장' }}
          </button>
        </div>
        <p v-if="sessionExtendMessage" class="session_feedback popup" role="status" aria-live="polite">
          {{ sessionExtendMessage }}
        </p>
        <span class="close_popup" @click="closePopup">팝업 닫기</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { SearchOverlay, SitemapDrawer } from '@/components/overlay/index.js'
import {
  useSessionTimer,
  initSessionTimer,
  clearSessionTimer,
  setSessionTimerByRemainingSeconds
} from '@/composables/useSessionTimer'
import { useUserStore } from '@/store/user.store'
import { customMemberLogout, getAccessTokenInfoCheck } from '@/service/auth.service'
import { countMenuAccess } from '@/service/stats.service'
import {
  handleSessionExpiredOnce,
  isDuplicateLoginInfo,
  isTokenExpiredInfo,
  isUnauthorizedError
} from '@/utils/sessionExpiry'
import { useAlert } from '@/composables/useAlert'

const { confirm, showAlert} = useAlert()


const isProd = import.meta.env.PROD
const server = import.meta.env.VITE_DEFAULT_SERVER_URL || ''

const router = useRouter()
const userStore = useUserStore()
const isExtendingSession = ref(false)
const sessionExtendMessage = ref('')
let sessionExtendMessageTimer = null

const SESSION_EXTEND_FEEDBACK_MODE_KEY = 'sessionExtendFeedbackMode'
const SESSION_EXTEND_COOLDOWN_UNTIL_KEY = 'sessionExtendCooldownUntil'
const SESSION_EXTEND_FORCE_MIN_INTERVAL_SEC = (() => {
  const raw = Number(import.meta.env.VITE_SESSION_EXTEND_FORCE_MIN_INTERVAL_SEC ?? 15)
  if (!Number.isFinite(raw) || raw <= 0) return 15
  return Math.floor(raw)
})()

const sessionExtendFeedbackMode = (() => {
  const runtime = String(sessionStorage.getItem(SESSION_EXTEND_FEEDBACK_MODE_KEY) || '').toLowerCase()
  const envMode = String(import.meta.env.VITE_SESSION_EXTEND_FEEDBACK_MODE || 'inline').toLowerCase()
  const mode = runtime || envMode
  return mode === 'alert' ? 'alert' : 'inline'
})()

const getSessionExtendCooldownRemainSeconds = () => {
  const until = Number(sessionStorage.getItem(SESSION_EXTEND_COOLDOWN_UNTIL_KEY) || 0)
  if (!Number.isFinite(until) || until <= 0) return 0
  const remainMs = until - Date.now()
  if (remainMs <= 0) return 0
  return Math.ceil(remainMs / 1000)
}

const setSessionExtendCooldownSeconds = (seconds) => {
  const sec = Number(seconds)
  if (!Number.isFinite(sec) || sec <= 0) {
    sessionStorage.removeItem(SESSION_EXTEND_COOLDOWN_UNTIL_KEY)
    return
  }
  sessionStorage.setItem(SESSION_EXTEND_COOLDOWN_UNTIL_KEY, String(Date.now() + Math.floor(sec) * 1000))
}

const clearSessionExtendCooldown = () => {
  sessionStorage.removeItem(SESSION_EXTEND_COOLDOWN_UNTIL_KEY)
}

const formatSecondsToMmss = (seconds) => {
  const sec = Math.max(0, Math.floor(Number(seconds) || 0))
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

const mmss = ref('--:--')
let mmssIntervalId = null

const updateMmss = () => {
  const expireAt = Number(sessionStorage.getItem('sessionExpireAt') || 0)
  if (!Number.isFinite(expireAt) || expireAt <= 0) {
    mmss.value = '--:--'
    return
  }
  const remainMs = expireAt - Date.now()
  const remainSec = Math.max(0, Math.floor(remainMs / 1000))
  mmss.value = formatSecondsToMmss(remainSec)
}

const startMmssTimer = () => {
  if (mmssIntervalId) clearInterval(mmssIntervalId)
  updateMmss()
  mmssIntervalId = setInterval(updateMmss, 1000)
}

const stopMmssTimer = () => {
  if (mmssIntervalId) {
    clearInterval(mmssIntervalId)
    mmssIntervalId = null
  }
}

const localCooldownMessage = (seconds) => `${formatSecondsToMmss(seconds)} 후 강제 연장 가능`
const serverGuardRejectedMessage = '잠시 후 재요청 바랍니다.'

const props = defineProps({
  isLoggedIn: { type: Boolean, default: false },
  menus: { type: Array, default: () => [] },
  selectedMenuId: { type: String, default: null }
})

async function logout() {
  clearSessionTimer()
  document.querySelector('.pop_logout')?.classList.remove('on')

  try {
    const result = await customMemberLogout()
    const redirectUrl = result?.redirectUrl || server

    sessionStorage.clear()
    userStore.logout()

    window.location.href = redirectUrl
  } catch {
    sessionStorage.clear()
    userStore.logout()
    window.location.href = server
  }
}

const timer = useSessionTimer({
  onExpire: () => {
    clearSessionTimer()
    handleSessionExpiredOnce('/login')
  },
  warningSec: 55,
  isEnabled: () => props.isLoggedIn === true
})

watch(
  () => props.isLoggedIn,
  (loggedIn) => {
    if (loggedIn) {
      if (!sessionStorage.getItem('sessionExpireAt')) initSessionTimer()
      timer.start()
      startMmssTimer()
    } else {
      timer.stop()
      clearSessionTimer()
      stopMmssTimer()
      mmss.value = '--:--'
      document.querySelector('.pop_logout')?.classList.remove('on')
    }
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  stopMmssTimer()
})

function closePopup() {
  document.querySelector('.pop_logout')?.classList.remove('on')
}

function handleExtendPopup() {
  if (isExtendingSession.value) {
    showSessionExtendMessage('처리 중입니다.')
    return
  }
  const remainCooldown = getSessionExtendCooldownRemainSeconds()
  if (remainCooldown > 0) {
    showSessionExtendMessage(localCooldownMessage(remainCooldown))
    return
  }
  syncSessionForce()
}

function handleExtend() {
  if (isExtendingSession.value) {
    showSessionExtendMessage('처리 중입니다.')
    return
  }
  const remainCooldown = getSessionExtendCooldownRemainSeconds()
  if (remainCooldown > 0) {
    showSessionExtendMessage(localCooldownMessage(remainCooldown))
    return
  }
  syncSessionForce()
}

const showSessionExtendMessage = async (message, ms = 1500) => {
  if (sessionExtendFeedbackMode === 'alert') {
    await showAlert(message)
    return
  }
  sessionExtendMessage.value = message
  if (sessionExtendMessageTimer) clearTimeout(sessionExtendMessageTimer)
  sessionExtendMessageTimer = setTimeout(() => {
    sessionExtendMessage.value = ''
  }, ms)
}

const extractRefreshApplied = (raw) => {
  if (!raw || typeof raw !== 'object') return null
  if (typeof raw.refreshApplied === 'boolean') return raw.refreshApplied
  if (typeof raw.refreshApplied === 'string') {
    const value = raw.refreshApplied.trim().toLowerCase()
    if (value === 'true' || value === 'y') return true
    if (value === 'false' || value === 'n') return false
  }
  if (raw.data && typeof raw.data === 'object' && typeof raw.data.refreshApplied === 'boolean') {
    return raw.data.refreshApplied
  }
  if (raw.data && typeof raw.data === 'object' && typeof raw.data.refreshApplied === 'string') {
    const value = raw.data.refreshApplied.trim().toLowerCase()
    if (value === 'true' || value === 'y') return true
    if (value === 'false' || value === 'n') return false
  }
  return null
}

const extractRefreshWaitSeconds = (raw) => {
  if (!raw || typeof raw !== 'object') return null
  const root = raw
  const data = raw.data && typeof raw.data === 'object' ? raw.data : {}
  const candidates = [
    root.guardRetryAfterSeconds,
    data.guardRetryAfterSeconds,
    root.refreshRetryAfterSeconds,
    data.refreshRetryAfterSeconds,
    root.retryAfterSeconds,
    data.retryAfterSeconds,
    root.refreshAvailableInSeconds,
    root.refreshCooldownSeconds,
    root.nextRefreshInSeconds,
    root.waitSeconds,
    data.refreshAvailableInSeconds,
    data.refreshCooldownSeconds,
    data.nextRefreshInSeconds,
    data.waitSeconds
  ]
  for (const v of candidates) {
    const n = Number(v)
    if (Number.isFinite(n) && n >= 0) return Math.floor(n)
  }
  return null
}

const extractResultCode = (raw) => {
  if (!raw || typeof raw !== 'object') return ''
  const code = raw.resultCode ?? raw?.data?.resultCode ?? ''
  return String(code || '')
    .trim()
    .toUpperCase()
}

const extractResultMessage = (raw) => {
  if (!raw || typeof raw !== 'object') return ''
  const message = raw.resultMessage ?? raw?.data?.resultMessage ?? raw.message ?? raw?.data?.message ?? ''
  return String(message || '').trim()
}

const extractRetryAfterSecondsFromMessage = (message) => {
  const text = String(message || '')
  if (!text) return null
  const match = text.match(/retryAfter\s*=\s*(\d+)\s*s?/i)
  if (!match) return null
  const sec = Number(match[1])
  return Number.isFinite(sec) && sec >= 0 ? Math.floor(sec) : null
}

const syncSessionForce = async () => {
  isExtendingSession.value = true
  const accessToken = sessionStorage.getItem('accessToken') || userStore.accessToken
  if (!accessToken) {
    showSessionExtendMessage('로그인 정보가 없어 연장할 수 없습니다.')
    closePopup()
    isExtendingSession.value = false
    return
  }
  if (!sessionStorage.getItem('accessToken')) {
    sessionStorage.setItem('accessToken', accessToken)
  }

  try {
    const info = await getAccessTokenInfoCheck({
      accessToken,
      refreshMode: 'FORCE'
    })

    if (isDuplicateLoginInfo(info)) {
      handleSessionExpiredOnce(info, '/login')
      return
    }

    if (isTokenExpiredInfo(info)) {
      handleSessionExpiredOnce(info, '/login')
      return
    }

    const remainSeconds = info?.remainingSeconds ?? null
    const refreshApplied = extractRefreshApplied(info?.raw)
    const refreshWaitSeconds = extractRefreshWaitSeconds(info?.raw)
    const resultCode = extractResultCode(info?.raw)
    const resultMessage = extractResultMessage(info?.raw)
    const resultMessageUpper = resultMessage.toUpperCase()
    const retryAfterFromMessage = extractRetryAfterSecondsFromMessage(resultMessage)

    const isCooldownCode =
      resultCode.includes('COOLDOWN') ||
      resultCode.includes('LOCK') ||
      resultCode.includes('FORCE_REFRESH_LOCKED') ||
      resultCode.includes('TOO_MANY') ||
      resultCode.includes('RETRY')

    const isCooldownMessage =
      resultMessageUpper.includes('COOLDOWN') ||
      resultMessageUpper.includes('LOCK') ||
      resultMessageUpper.includes('FORCE_REFRESH_LOCKED') ||
      resultMessageUpper.includes('TOO_MANY') ||
      resultMessageUpper.includes('RETRY')

    const isForceApplied = refreshApplied === true
    const hasGuardSignal = isCooldownCode || isCooldownMessage
    const cooldownSeconds = Number.isFinite(refreshWaitSeconds) ? refreshWaitSeconds : retryAfterFromMessage

    const shouldRetry = !isForceApplied && (hasGuardSignal || Number.isFinite(cooldownSeconds))

    if (isForceApplied) {
      const nextCooldown =
        Number.isFinite(cooldownSeconds) && cooldownSeconds > 0
          ? cooldownSeconds
          : SESSION_EXTEND_FORCE_MIN_INTERVAL_SEC
      if (nextCooldown > 0) setSessionExtendCooldownSeconds(nextCooldown)
      else clearSessionExtendCooldown()
    } else if (Number.isFinite(cooldownSeconds) && cooldownSeconds > 0) {
      setSessionExtendCooldownSeconds(cooldownSeconds)
    }

    if (remainSeconds !== null) {
      setSessionTimerByRemainingSeconds(remainSeconds)
      sessionStorage.removeItem('sessionWarned')
      if (isForceApplied) {
        const successCooldown = getSessionExtendCooldownRemainSeconds()
        if (successCooldown > 0) showSessionExtendMessage(localCooldownMessage(successCooldown))
        else showSessionExtendMessage('연장되었습니다.')
      } else if (shouldRetry) {
        showSessionExtendMessage(serverGuardRejectedMessage)
      } else {
        showSessionExtendMessage('세션 연장에 실패했습니다. 잠시 후 재시도해 주세요.')
      }
    } else {
      showSessionExtendMessage(
        shouldRetry ? serverGuardRejectedMessage : '세션 연장에 실패했습니다. 잠시 후 재시도해 주세요.'
      )
    }
  } catch (e) {
    if (isUnauthorizedError(e)) {
      handleSessionExpiredOnce(e?.response?.data ?? e, '/login')
      return
    }
    showSessionExtendMessage(e?.serverMessage || '잠시 후 재시도해 주세요.')
  } finally {
    isExtendingSession.value = false
    closePopup()
  }
}

const emit = defineEmits(['login', 'logout', 'extend-session', 'menu-change'])

const isVisibleMenu = (menu) => menu?.menuIndctYn === 'Y' || menu?.menuIndctYn === 'YES'

const visibleChildren = (children) => {
  const list = Array.isArray(children) ? children : []
  return list.filter(isVisibleMenu)
}

const headerNav = computed(() => {
  const navData = props.menus || []
  return navData.filter(isVisibleMenu).map((s, idx) => ({
    ...s,
    titAreaClass: `tit_area tit_area${String(idx + 1).padStart(2, '0')}`
  }))
})

const normalizeLink = (raw) => {
  const link = String(raw || '').trim()
  if (!link || link === 'dir' || link === 'N') return null
  if (/^https?:\/\//i.test(link)) return link
  return link.startsWith('/') ? link : `/${link}`
}

const findFirstNavigableHref = (nodes) => {
  if (!Array.isArray(nodes)) return null
  for (const node of nodes) {
    const href = normalizeLink(node?.path) || normalizeLink(node?.menuLink)
    if (href) return href
    const childHref = findFirstNavigableHref(node.children)
    if (childHref) return childHref
  }
  return null
}

const hrefForMenu = (menu) => {
  const direct = normalizeLink(menu?.path) || normalizeLink(menu?.menuLink)
  if (direct) return direct
  if (menu?.children?.length > 0) {
    return findFirstNavigableHref(menu.children)
  }
  return null
}

const navigate = async (menu) => {
  const href = hrefForMenu(menu)
  if (!href) return

  // 메뉴 접속수 카운트
  if (menu.menuId) {
    countMenuAccess({ menuUrl: href, menuId: menu.menuId }).catch(() => {})
  }

  emit('menu-change', menu.menuId)

  const isExternal = /^https?:\/\//i.test(href)
  const openInNewTab = menu?.target === '_blank' || menu?.menuType === 'LINK'

  if (isExternal && openInNewTab) {
    window.open(href, '_blank', 'noopener')
    return
  }

  if (isExternal) {
    window.location.href = href
    return
  }

  await router.push(href)
}

const relFor = (item) => item?.rel || (item?.target === '_blank' ? 'noopener noreferrer' : undefined)

const defaultTagline = `<span>한</span>번에! <span>쉽</span>게! <span>간</span>편하게! <br>대구시 바로예약 서비스`

const isSearchOpen = ref(false)
const isSitemapOpen = ref(false)

let gnbCleanup = null

const setupGnb = () => {
  if (gnbCleanup) {
    gnbCleanup()
    gnbCleanup = null
  }

  const header = document.getElementById('header')
  if (!header) return

  const gnbWrap = header.querySelector('.gnb_wrap')
  const gnb = gnbWrap?.querySelector('#gnb')
  if (!gnb) return

  let gnbBg = header.querySelector('.gnb_bg')
  if (!gnbBg) {
    gnbBg = document.createElement('div')
    gnbBg.className = 'gnb_bg'
    header.appendChild(gnbBg)
  }

  const gnbItems = Array.from(gnb.children).filter((el) => el.tagName === 'LI')
  if (!gnbItems.length) return

  const gnbAnchors = gnbItems.map((li) => li.querySelector(':scope > a')).filter(Boolean)
  const gnbDivs = gnbItems.map((li) => li.querySelector(':scope > div')).filter(Boolean)

  const gnbType = 'self_wide'

  function computeHeights() {
    let max = 0
    let maxIdx = 0

    gnbDivs.forEach((div, i) => {
      const prev = {
        display: div.style.display,
        height: div.style.height,
        overflow: div.style.overflow
      }
      div.style.display = 'block'
      div.style.height = 'auto'
      div.style.overflow = ''

      const h = div.offsetHeight
      const capped = Math.min(h, window.innerHeight - 150)
      div.dataset.height = String(capped)

      if (h > max) {
        max = h
        maxIdx = i
      }

      div.style.display = 'block'
      div.style.height = '0px'
      div.style.overflow = prev.overflow ?? ''
    })

    const maxDiv = gnbDivs[maxIdx]
    const gnbHeight = maxDiv ? parseInt(maxDiv.dataset.height || '0', 10) : 0

    gnbBg.style.display = 'block'
    gnbBg.style.height = '0px'
    return gnbHeight
  }

  let maxHeight = computeHeights()

  function openForLi(li) {
    header.classList.add('open')
    gnbBg.classList.add('open')

    if (gnbType === 'whole') {
      gnbItems.forEach((item, idx) => {
        item.classList.add('open')
        item.classList.remove('active')
        const div = gnbDivs[idx]
        if (div) div.style.height = `${maxHeight}px`
      })
      gnbBg.style.height = `${maxHeight}px`
      return
    }

    const div = li.querySelector(':scope > div')
    const selfHeight = div ? parseInt(div.dataset.height || '0', 10) : 0

    gnbItems.forEach((item, idx) => {
      item.classList.remove('open', 'active')
      const dv = gnbDivs[idx]
      if (dv) dv.style.height = '0px'
    })

    li.classList.add('open', 'active')
    let p = li.parentElement
    while (p && p !== gnb) {
      if (p.tagName === 'LI') p.classList.add('active')
      p = p.parentElement
    }

    if (div) {
      div.style.display = 'block'
      div.style.height = `${selfHeight}px`
    }
    gnbBg.style.display = 'block'
    gnbBg.style.height = `${selfHeight}px`
  }

  let closeTimer = null

  function closeAll() {
    if (closeTimer) {
      clearTimeout(closeTimer)
      closeTimer = null
    }

    gnbDivs.forEach((div) => (div.style.height = '0px'))
    gnbBg.style.height = '0px'

    closeTimer = setTimeout(() => {
      header.classList.remove('open')
      gnbItems.forEach((li) => {
        li.classList.remove('open', 'active')
        const dv = li.querySelector(':scope > div')
        if (dv) dv.style.height = '0px'
      })
      gnbBg.classList.remove('open')
      onScroll()
      closeTimer = null
    }, 1)
  }

  const unbind = []

  gnbAnchors.forEach((a) => {
    const enter = (e) => {
      if (closeTimer) {
        clearTimeout(closeTimer)
        closeTimer = null
      }
      const li = e.currentTarget.closest('li')
      if (li) openForLi(li)
    }
    a.addEventListener('mouseenter', enter)
    a.addEventListener('focus', enter)
    unbind.push(() => {
      a.removeEventListener('mouseenter', enter)
      a.removeEventListener('focus', enter)
    })
  })

  gnbDivs.forEach((div) => {
    const enter = (e) => {
      if (closeTimer) {
        clearTimeout(closeTimer)
        closeTimer = null
      }
      const li = e.currentTarget.closest('li')
      if (li) openForLi(li)
    }
    div.addEventListener('mouseenter', enter)
    unbind.push(() => div.removeEventListener('mouseenter', enter))
  })

  const leaveGnb = () => closeAll()
  gnbWrap?.addEventListener('mouseleave', leaveGnb)
  unbind.push(() => gnbWrap?.removeEventListener('mouseleave', leaveGnb))

  const onFocusIn = (e) => {
    if (!gnbWrap?.contains(e.target)) closeAll()
  }
  document.addEventListener('focusin', onFocusIn)
  unbind.push(() => document.removeEventListener('focusin', onFocusIn))

  const onScroll = () => {
    const top = window.scrollY || document.documentElement.scrollTop || 0
    if (top > 10) {
      document.body.classList.add('fixed')
      header.classList.add('open')
    } else {
      document.body.classList.remove('fixed')
      header.classList.remove('open')
    }
  }
  window.addEventListener('scroll', onScroll)
  unbind.push(() => window.removeEventListener('scroll', onScroll))
  setTimeout(onScroll, 0)

  const onResize = () => {
    maxHeight = computeHeights()
  }
  window.addEventListener('resize', onResize)
  unbind.push(() => window.removeEventListener('resize', onResize))

  gnbCleanup = () => {
    if (closeTimer) {
      clearTimeout(closeTimer)
      closeTimer = null
    }
    unbind.forEach((fn) => fn())
  }
}

onMounted(async () => {
  const pkceCode = sessionStorage.getItem('pkce_code')
  if (pkceCode && !props.isLoggedIn) {
    emit('login')
  }

  await nextTick()
  setupGnb()
})

watch(
  () => props.menus,
  async () => {
    await nextTick()
    setupGnb()
  },
  { deep: true }
)

onBeforeUnmount(() => {
  if (gnbCleanup) gnbCleanup()
  if (sessionExtendMessageTimer) clearTimeout(sessionExtendMessageTimer)
})
</script>

<style scoped>
.session_feedback {
  margin-left: 8px;
  font-size: 12px;
  color: #0f4c81;
}
.session_feedback.popup {
  margin: 10px 0 0;
  text-align: center;
}

/* .area_top > .inr{
  justify-content: space-between !important;
}

.area_top > .inr > p{
  font-weight: bold;
  color: #a5a5a5;
  padding: 4px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

@media (max-width: 1023px) {
  
.area_top > .inr{
  flex-direction: column;
  justify-content: flex-end !important;
}

.area_top > .inr > p{
  font-size: 13px;
  text-align: center;
  margin: 0 auto 4px auto;
}

} */
</style>
